// Modal — centered overlay on the React Aria foundation (ADR-029), per
// COMPONENTS §Modal vs SlideOver. React Aria owns the behavior we used to
// hand-roll: focus trap, focus return to the trigger, Escape + backdrop close,
// body scroll lock, role="dialog" + aria-modal, and RTL-correct interaction. We
// own 100% of the markup + tokens (COMPONENTS §Hand-rolled vs headless).
//
// Use for destructive confirms and single-question prompts; create/edit forms
// use <SlideOver>. The public API is unchanged from the hand-rolled version
// (controlled `open` + `onClose`), so call sites don't change.
//
// TWO FACES, one foundation:
//   • the CARD (default) — the centered dialog with its title row + footer.
//   • `fullscreen` — the dialog fills the viewport edge to edge with NO built-in
//     chrome: the consumer owns the whole surface (a file's stage, the phone's
//     second screen — FRONTEND-PATTERNS §Files). `title` becomes the dialog's
//     accessible name; `className` paints the ground (the stage token for a
//     picture, the page tokens for a second screen). Focus trap · Escape ·
//     scroll-lock still come from the foundation, so a second dialog engine is
//     never needed for "big".
//
// BOTH faces are sized to the VISIBLE screen (the foundation's
// `--visual-viewport-height`, set on the overlay from `window.visualViewport`):
// on a phone the browser's bars and the keyboard eat the bottom of a `vh` box —
// a dialog's footer would sit under them. The card stays a centred card
// (ADR-080 §12), its height capped to what is visible, its body scrolling.

import { useId, type ReactNode } from 'react';
import { ModalOverlay, Modal as AriaModal, Dialog, Heading } from 'react-aria-components';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';
import { IconButton } from './IconButton.js';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Accessible title — wired via aria-labelledby (React Aria Heading slot);
   *  the dialog's `aria-label` on the fullscreen face. */
  title: string;
  /** Optional description for aria-describedby (card face only). */
  description?: string;
  children: ReactNode;
  /** Footer slot (Cancel/Confirm buttons) — card face only. */
  footer?: ReactNode;
  /** Tailwind max-w-* class. Default: max-w-md (card face only). */
  maxWidth?: string;
  /** The FULLSCREEN face — edge to edge, no title row, no footer, no padding:
   *  the consumer renders the whole surface incl. its own close control. */
  fullscreen?: boolean;
  /** fullscreen only — the surface classes (the ground). */
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  maxWidth = 'max-w-md',
  fullscreen = false,
  className = '',
}: ModalProps) {
  const descId = useId();
  const { t } = useTranslation('common');

  if (fullscreen) {
    return (
      <ModalOverlay
        isOpen={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) onClose();
        }}
        className="fixed inset-x-0 top-0 h-[var(--visual-viewport-height)] z-overlay data-[entering]:o-fade-in data-[exiting]:o-fade-out"
      >
        <AriaModal className={`h-full w-full ${className}`}>
          <Dialog aria-label={title} className="flex h-full min-h-0 flex-col outline-none">
            {children}
          </Dialog>
        </AriaModal>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      isDismissable
      className="fixed inset-x-0 top-0 h-[var(--visual-viewport-height)] z-overlay flex items-center justify-center p-4 bg-[var(--color-overlay)] data-[entering]:o-scrim-in data-[exiting]:o-scrim-out"
    >
      <AriaModal
        className={`w-full ${maxWidth} bg-[var(--color-bg-overlay)] text-[var(--color-text-primary)] rounded-lg shadow-modal border border-[var(--color-border)] flex flex-col max-h-full data-[entering]:o-modal-in data-[exiting]:o-modal-out`}
      >
        <Dialog
          aria-describedby={description ? descId : undefined}
          className="outline-none flex flex-col min-h-0"
        >
          <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[var(--color-border)]">
            {/* min-w-0 + wrap-anywhere: a long unbreakable title (a file name, a URL) folds inside the card, never past it. */}
            <Heading slot="title" className="min-w-0 wrap-anywhere text-base font-semibold">
              {title}
            </Heading>
            <IconButton aria-label={t('actions.close')} onClick={onClose} variant="ghost">
              <icons.close aria-hidden size={18} />
            </IconButton>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {description && (
              <p id={descId} className="text-sm text-[var(--color-text-secondary)] mb-3">
                {description}
              </p>
            )}
            {children}
          </div>
          {footer && (
            <footer className="px-5 py-3 border-t border-[var(--color-border)] flex justify-end gap-2">
              {footer}
            </footer>
          )}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
}
